/**
 * <AdaptProgramSheet> — « Adapter le programme » : la famille dit en une phrase ce que l'enfant a
 * déjà vu, l'avance à prendre ou un objectif précis (organism).
 *
 * Une phrase, trois débuts de phrase à toucher, un repère (le programme officiel), « Recomposer ». La feuille ne demande que la phrase : la recomposition se joue
 * ensuite sur le programme lui-même (`<StageProgramPage>`). Feuille du bas sur un téléphone,
 * fenêtre centrée sur un grand écran (`<Modal>`).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"

/**
 * The starts of a sentence the family touches to add. What was seen in class is the usual case, not
 * the only one: the sentence above names getting ahead and any precise goal too.
 */
export const ADAPT_PROGRAM_EXAMPLES = [
    { label: "Déjà vu", text: "Déjà vu en classe : " },
    { label: "Pas encore vu", text: "Pas encore vu : " },
    { label: "Prendre de l'avance", text: "Prendre de l'avance sur " },
] as const

export type AdaptProgramSheetProps = {
    visible: boolean
    onClose: () => void
    draft: string
    onChangeDraft: (text: string) => void
    onAddExample: (text: string) => void
    onRecompose: () => void
    /** A sentence is written, and it is not the one in place. */
    canRecompose: boolean
    childFirstname: string | null
    /** The last recomposition failed — said here, above the button. */
    errorMessage?: string | null
    testID?: string
}

export function AdaptProgramSheet(props: AdaptProgramSheetProps) {
    const {
        visible,
        onClose,
        draft,
        onChangeDraft,
        onAddExample,
        onRecompose,
        canRecompose,
        childFirstname,
        errorMessage,
        testID,
    } = props

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title="Adapter le programme"
            sizeToContent
            testID={testID}
            footer={<Button label="Recomposer" fullWidth onPress={onRecompose} disabled={!canRecompose} />}
        >
            <Stack gap="4">
                <Text size="text-md" color="text-muted">
                    {`Ce ${elide(childFirstname ?? "votre enfant", "que")} a déjà vu, l'avance à prendre, un objectif précis : on recompose sa semaine.`}
                </Text>
                <TextInput
                    value={draft}
                    onChangeText={onChangeDraft}
                    multiline
                    numberOfLines={4}
                    placeholder="Ex. : Pythagore déjà vu, de l'avance sur les fonctions"
                />
                <Row gap="2" style={styles.wrap}>
                    {ADAPT_PROGRAM_EXAMPLES.map((example) => (
                        <ChoiceChip
                            key={example.label}
                            variant="pill"
                            selected={false}
                            iconName="plus"
                            label={example.label}
                            onPress={() => onAddExample(example.text)}
                        />
                    ))}
                </Row>
                <Row gap="4" style={styles.wrap}>
                    <Row gap="1" align="center">
                        <Icon name="check-circle" size={14} color="text-muted" />
                        <Text size="text-sm" color="text-muted">
                            Programme officiel
                        </Text>
                    </Row>
                </Row>
                {errorMessage != null && (
                    <Banner message={errorMessage} icon="exclamation-triangle" color="danger" tinted />
                )}
            </Stack>
        </Modal>
    )
}

const styles = StyleSheet.create(() => ({
    wrap: {
        flexWrap: "wrap",
    },
}))
